<template>
    <v-menu bottom left>
        <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" class="tour-trigger-btn settings-btn">
                <v-icon color="#666">mdi-help-circle-outline</v-icon>
            </v-btn>
        </template>
        
        <v-card min-width="280" elevation="8">
            <v-card-title class="py-3">
                <v-icon left color="primary">mdi-school-outline</v-icon>
                <span class="font-weight-bold">Guided Tours</span>
            </v-card-title>
            <v-divider></v-divider>
            
            <v-list dense>
                <v-list-item @click="startTour('essentials')" :disabled="isCurrentlyOnTour">
                    <v-list-item-icon>
                        <v-icon :color="hasCompletedEssentials ? 'success' : 'primary'">
                            {{ hasCompletedEssentials ? 'mdi-check-circle' : 'mdi-play-circle' }}
                        </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title class="font-weight-medium">Essential Features</v-list-item-title>
                        <v-list-item-subtitle>Learn the basics of task management</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action v-if="hasCompletedEssentials">
                        <v-icon small color="success">mdi-check</v-icon>
                    </v-list-item-action>
                </v-list-item>
                
                <v-list-item @click="startTour('advanced')" :disabled="!hasCompletedEssentials || isCurrentlyOnTour">
                    <v-list-item-icon>
                        <v-icon :color="getIconColor('advanced')">
                            {{ hasCompletedAdvanced ? 'mdi-check-circle' : 'mdi-rocket-launch' }}
                        </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title class="font-weight-medium" :class="{ 'text--disabled': !hasCompletedEssentials }">
                            Advanced Features
                        </v-list-item-title>
                        <v-list-item-subtitle :class="{ 'text--disabled': !hasCompletedEssentials }">
                            Bulk operations, filters & shortcuts
                        </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action v-if="hasCompletedAdvanced">
                        <v-icon small color="success">mdi-check</v-icon>
                    </v-list-item-action>
                </v-list-item>
                
                <v-list-item 
                    v-if="isAdminOrManager" 
                    @click="startTour('admin')" 
                    :disabled="!hasCompletedAdvanced || isCurrentlyOnTour">
                    <v-list-item-icon>
                        <v-icon :color="getIconColor('admin')">
                            {{ hasCompletedAdmin ? 'mdi-check-circle' : 'mdi-shield-crown' }}
                        </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title class="font-weight-medium" :class="{ 'text--disabled': !hasCompletedAdvanced }">
                            Admin Features
                        </v-list-item-title>
                        <v-list-item-subtitle :class="{ 'text--disabled': !hasCompletedAdvanced }">
                            Team management & oversight
                        </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action v-if="hasCompletedAdmin">
                        <v-icon small color="success">mdi-check</v-icon>
                    </v-list-item-action>
                </v-list-item>
                
                <v-divider class="my-2"></v-divider>
                
                <!-- Tour Progress -->
                <v-list-item class="tour-progress-item">
                    <v-list-item-content>
                        <div class="d-flex align-center justify-space-between">
                            <span class="caption font-weight-medium">Progress</span>
                            <span class="caption">{{ completionStats.completed }}/{{ completionStats.total }}</span>
                        </div>
                        <v-progress-linear 
                            :value="progressPercentage" 
                            color="primary" 
                            height="6" 
                            rounded
                            class="mt-1">
                        </v-progress-linear>
                    </v-list-item-content>
                </v-list-item>
                
                <v-divider class="my-2"></v-divider>
                
                <!-- Reset Tours -->
                <v-list-item @click="resetAllTours" :disabled="isCurrentlyOnTour">
                    <v-list-item-icon>
                        <v-icon color="grey">mdi-refresh</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title class="font-weight-medium">Reset All Tours</v-list-item-title>
                        <v-list-item-subtitle>Start over from the beginning</v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
                
                <!-- Exit Current Tour -->
                <v-list-item v-if="isCurrentlyOnTour" @click="exitCurrentTour">
                    <v-list-item-icon>
                        <v-icon color="error">mdi-exit-to-app</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title class="font-weight-medium">Exit Current Tour</v-list-item-title>
                        <v-list-item-subtitle>Stop the active tour</v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-card>
    </v-menu>
</template>

<script>
import { mapGetters } from 'vuex'
import tourService from '@/services/tourService'

export default {
    name: 'TourTrigger',
    
    data() {
        return {
            isCurrentlyOnTour: false
        }
    },
    
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user'
        }),
        
        isAdminOrManager() {
            return this.get_user && [1, 2, 11].includes(this.get_user.role_id)
        },
        
        hasCompletedEssentials() {
            return tourService.hasCompletedTour('essentials')
        },
        
        hasCompletedAdvanced() {
            return tourService.hasCompletedTour('advanced')
        },
        
        hasCompletedAdmin() {
            return tourService.hasCompletedTour('admin')
        },
        
        completionStats() {
            return tourService.getTourStats()
        },
        
        progressPercentage() {
            if (this.completionStats.total === 0) return 0
            return (this.completionStats.completed / this.completionStats.total) * 100
        }
    },
    
    methods: {
        startTour(tourName) {
            if (this.isCurrentlyOnTour) {
                this.$toast.warning('Please finish or exit the current tour first')
                return
            }
            
            // Check prerequisites
            if (tourName === 'advanced' && !this.hasCompletedEssentials) {
                this.$toast.info('Complete the Essential Features tour first')
                return
            }
            
            if (tourName === 'admin' && !this.hasCompletedAdvanced) {
                this.$toast.info('Complete the Advanced Features tour first')
                return
            }
            
            this.isCurrentlyOnTour = true
            
            try {
                const intro = tourService.startTour(tourName)
                
                // Listen for tour completion or exit
                if (intro) {
                    intro.oncomplete(() => {
                        this.isCurrentlyOnTour = false
                    })
                    
                    intro.onexit(() => {
                        this.isCurrentlyOnTour = false
                    })
                }
            } catch (error) {
                console.error('Failed to start tour:', error)
                this.$toast.error('Failed to start tour')
                this.isCurrentlyOnTour = false
            }
        },
        
        resetAllTours() {
            if (this.isCurrentlyOnTour) {
                this.$toast.warning('Please finish or exit the current tour first')
                return
            }
            
            // Simple confirmation without external dialog dependency
            if (confirm('Are you sure you want to reset all tour progress? You can retake any tour after this.')) {
                tourService.resetTours()
                this.$toast.success('All tours have been reset. You can now retake any tour.')
            }
        },
        
        exitCurrentTour() {
            tourService.exitCurrentTour()
            this.isCurrentlyOnTour = false
            this.$toast.info('Tour exited')
        },
        
        getIconColor(tourName) {
            if (tourName === 'advanced') {
                return this.hasCompletedEssentials ? 
                    (this.hasCompletedAdvanced ? 'success' : 'primary') : 
                    'grey'
            }
            
            if (tourName === 'admin') {
                return this.hasCompletedAdvanced ? 
                    (this.hasCompletedAdmin ? 'success' : 'primary') : 
                    'grey'
            }
            
            return 'primary'
        }
    }
}
</script>

<style scoped>
.tour-trigger-btn {
    transition: all 0.2s ease;
    color: #666 !important;
}

.tour-trigger-btn:hover {
    background-color: rgba(0, 0, 0, 0.04) !important;
}

.tour-trigger-btn .v-icon {
    color: #666 !important;
}

.tour-trigger-btn:hover .v-icon {
    color: #333 !important;
}

.tour-progress-item {
    pointer-events: none;
}

.tour-progress-item .v-list-item__content {
    padding: 8px 0;
}

.v-list-item--disabled {
    pointer-events: none;
}

.v-list-item--disabled .v-list-item__icon .v-icon {
    color: rgba(0, 0, 0, 0.26) !important;
}
</style>